import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, BookOpen, Eye, EyeOff } from "lucide-react";

const SPLIT_BG =
  "https://images.unsplash.com/photo-1532456745301-b2c645d8b80d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxzdWJ0bGUlMjBnZW9tZXRyaWMlMjBwYXR0ZXJuJTIwYmFja2dyb3VuZCUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODA5NTQ0NTF8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(username.trim(), password);
      if (user.must_change_password) {
        navigate("/change-password", { replace: true });
      } else {
        navigate(user.role === "admin" ? "/admin" : "/teacher", { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: form */}
      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-16">
        <div className="w-full max-w-md mx-auto">
          <div className="flex items-center gap-3 mb-10">
            <div className="h-11 w-11 rounded-lg bg-primary flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
              <p className="font-heading text-lg font-semibold leading-tight text-foreground">
                Al-Bushra
              </p>
              <p className="text-sm text-muted-foreground leading-tight">Quran School</p>
            </div>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-foreground">
            Sign in
          </h1>
          <p className="mt-2 text-muted-foreground">
            Access the school management dashboard.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                className="h-12"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  className="h-12 pr-11"
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  data-testid="login-toggle-password"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm rounded-lg bg-destructive/10 text-destructive px-4 py-3 border border-destructive/20"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-12 text-base"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
            </Button>
          </form>
        </div>
      </div>

      {/* Right: brand pane */}
      <div className="hidden lg:block relative bg-primary overflow-hidden">
        <img
          src={SPLIT_BG}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-luminosity"
        />
        <div className="relative h-full flex flex-col justify-end p-14">
          <div className="h-1 w-16 bg-[hsl(var(--gold))] mb-6" />
          <h2 className="font-heading text-4xl font-medium text-white leading-tight">
            Al-Bushra Quran School
          </h2>
          <p className="mt-4 text-white/70 max-w-md text-lg">
            Attendance, memorization progress, points, and teacher hours — all in
            one place.
          </p>
        </div>
      </div>
    </div>
  );
}
